import React, { useEffect, useState } from "react";
import PingStatusTooltip from "../../../Components/PingStatus/PingStatusTooltip";
import { Dialog, DialogContent, Box, Typography, Grid2, Divider } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { styled } from "@mui/material/styles";
import CloseButton from "../../../Components/CloseButton";
import TextWithCopy from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import { useTranslation } from "react-i18next";
import { getMetricTimeStamp } from "../../../Helper/DatePicker/DateConverter";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { getRealTimeInfo } from "../../../Api/DigitalTwin";
import { getVehicleInfo } from "../../../Api/VehicleApi";
import { getDigitalTwinList } from "../../../Api/DigitalTwinListApi";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    width: "798px",
    maxWidth: "100%",
    margin: 0,
    maxHeight: "100%",
  },
}));

const CustomMoreDetailsDialog = ({battery}) => {
  const { t } = useTranslation(['common', 'digital_twin']);

  const [batteryInfo, setBatteryInfo] = useState({});
  const [vehicleInfo, setVehicleInfo] = useState({});
  const [digitalTwinInfo, setDigitalTwinInfo] = useState({});
  const [digitalTwinData, setDigitalTwinData] = useState({});

  const [open, setOpen] = useState(false);

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  const fetchBatteryInfo = async () => {
		try {
			const [realTimeRes, vehicleInfoRes, digitalTwinRes] = await Promise.all([
				getRealTimeInfo(battery.batteryID),
				getVehicleInfo(battery.batteryID),
        getDigitalTwinList({ pageNumber: 1, rowsPerPage: 1, filters: { batteryID: [battery.batteryID] } }),
			]);

			setBatteryInfo(realTimeRes?.data || {});
			setVehicleInfo(vehicleInfoRes?.response?.data || {});
      setDigitalTwinInfo(digitalTwinRes?.response?.data?.[0] || {});
		} catch (error) {
			console.error('Error fetching digital twin hierarchy:', error);
		}
	};

  useEffect(() => {
    if (open) {
      fetchBatteryInfo();
    }
  }, [open])

  useEffect(() => {
    setDigitalTwinData({
      soc: ![undefined, null].includes(digitalTwinInfo?.soc) ? `${digitalTwinInfo?.soc} %` : '-',
      temperature: ![undefined, null].includes(digitalTwinInfo?.temperature) ? `${digitalTwinInfo?.temperature} °C` : '-',
      current: ![undefined, null].includes(digitalTwinInfo?.current) ? `${digitalTwinInfo?.current} A` : '-',
      voltage: ![undefined, null].includes(digitalTwinInfo?.voltage) ? `${digitalTwinInfo?.voltage} V` : '-',
    })
  }, [digitalTwinInfo])

  return (
    <>
      <Box
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      >
        <Typography
          variant="fw500s12c000"
          onClick={handleOpen}
          onFocus={(e) => e.stopPropagation()}
          sx={{
            lineHeight: "14px",
            '&:hover': {
              color: '#6D72F6'
            }
          }}
        >
          Metrics & Vehicle Details
        </Typography>
      </Box>

      <StyledDialog
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      >
        <DialogContent sx={{ py: 3 }}>
          <Grid2 sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="fw500s16lh24c000000" sx={{ fontSize: "18px" }}>
                More Details
              </Typography>
              <CloseButton onClick={handleClose} />
            </Box>
            <Grid2 container gap={5.5}>
              <Box sx={{flex: 1}}>
                <Box sx={{mb: 1}}>
                  <Typography variant="fw500s14c000000" sx={{lineHeight: '24px'}}>Latest Recorded Metrics</Typography>
                </Box>
                <Grid2 container flexDirection={'column'} gap={0.5}>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Ping Status:</Typography>
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5}}>
                      <PingStatusTooltip online={batteryInfo?.pingStatus?.toLowerCase() === 'active'}>
                        <FiberManualRecordIcon sx={{ color: batteryInfo?.pingStatus?.toLowerCase() === 'active' ? "#09B820" : "#8F8F8F" }} />
                      </PingStatusTooltip>
                      <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px', textTransform: 'capitalize' }}>{batteryInfo?.pingStatus?.toLowerCase() === 'active' ? t("common:online") : t("common:offline")}</Typography>
                    </Box>
                  </Grid2>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Last Pinged:</Typography>
                    {batteryInfo?.lastPingedTime ?
                      <PrimaryTooltip title={getMetricTimeStamp(batteryInfo.lastPingedTime)} placement="bottom">
                          <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{getMetricTimeStamp(batteryInfo.lastPingedTime, 'relative')}</Typography>
                      </PrimaryTooltip> : <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>NA</Typography>
                    }
                  </Grid2>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>BMS Status:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{batteryInfo?.batteryStatus}</Typography>
                  </Grid2>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>SOC:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{digitalTwinData?.soc}</Typography>
                  </Grid2>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Battery System Temp:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{digitalTwinData?.temperature}</Typography>
                  </Grid2>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Battery System Current:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{digitalTwinData?.current}</Typography>
                  </Grid2>
                  <Grid2 container justifyContent={'space-between'} alignItems={'center'} width={'100%'} pt={0.5} pb={1} sx={{borderBottom: '1px solid #E9E9E9'}}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Battery System Voltage:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{digitalTwinData?.voltage}</Typography>
                  </Grid2>
                </Grid2>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: "#E9E9E9", marginLeft: '12px' }}
              />
              <Box sx={{flex: 1}}>
                <Box sx={{mb: 1}}>
                  <Typography variant="fw500s14c000000" sx={{lineHeight: '24px'}}>Vehicle Details</Typography>
                </Box>
                <Grid2 container gap={1} sx={{ marginTop: 1.5, paddingTop: 1, paddingBottom: 1, flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Bus Number:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{vehicleInfo?.licensePlateNo ?? '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Depot:</Typography>
                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{vehicleInfo?.originDepo ?? '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>IMEI Number:</Typography>
                    {batteryInfo?.deviceID ? (
                      <TextWithCopy text={batteryInfo?.deviceID} type={'IMEI Number'} textVariant="fw400s14c000000" />
                    ) : (
                      <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{'-'}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: '24px' }}>Chassis Number:</Typography>
                    {vehicleInfo?.vin ? (
                      <TextWithCopy text={vehicleInfo?.vin} type={'Chassis Number'} textVariant="fw400s14c000000" />
                    ) : (
                      <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px' }}>{'-'}</Typography>
                    )}
                  </Box>
                </Grid2>
              </Box>
            </Grid2>
          </Grid2>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default CustomMoreDetailsDialog;
